import type { ApiErrorBody, AppSurface, EngageInboxNetwork, ErrorCode, SocialNetwork, WorkspaceApp } from "@bafe/types";
import { SURFACE_HEADER, csrfCookieName } from "@bafe/types";

import type {
	AbuseEventDetailDto,
	AbuseEventDto,
	AdminConfigurationDto,
	AdminCustomerDto,
	AdminDomainDto,
	AdminFeatureFlagDto,
	AdminMailboxDto,
	AdminMigrationDto,
	AdminNoteDto,
	AdminOverviewDto,
	AdminProvisioningJobDto,
	AdminSettingDto,
	AdminSubscriptionDto,
	AdminUserDetailDto,
	AdminUserDto,
	AdminSupportCaseDto,
	AliasDto,
	ApiEnvelope,
	AuditEntryDto,
	CloudflarePreviewDto,
	DomainDetailDto,
	DomainDto,
	CustomerDatabaseDto,
	CustomerDatabaseListDto,
	HostedSiteDto,
	HubRecordsDto,
	HubTaskAttachmentItem,
	HubTaskChecklistItem,
	HubTaskCommentDto,
	IncidentDto,
	SiteFileListDto,
	SiteFileWriteDto,
	SiteRedirectDto,
	SiteTagDto,
	SiteTextFileDto,
	SiteUploadResultDto,
	IncidentStatus,
	InvoiceDto,
	MailHealthDto,
	AccessibleMailboxDto,
	MailInboxCounterDto,
	UnifiedThreadListDto,
	MailboxDetailDto,
	MailboxDto,
	MailboxGrantDto,
	MessageOpenDto,
	UserMessageReadDto,
	InboxGroupingDto,
	MailSignaturesDto,
	MailContactDto,
	MailClientSettingsDto,
	MailClientSettingsTestDto,
	SidebarAccountOrderDto,
	SidebarGroupDto,
	MailboxSecretDto,
	InvitationPreviewDto,
	MemberDto,
	MigrationDto,
	MigrationTestResultDto,
	OrganisationDto,
	OverviewDto,
	PageEnvelope,
	PlanDto,
	ProvisioningJobDetailDto,
	RecoveryCodesDto,
	SessionDeviceDto,
	SessionDto,
	StatusPageComponent,
	StorageDto,
	StorageTrendDto,
	SubscriptionDto,
	WorkspaceConfigDto,
	WorkspaceSiteDto,
	TeamChannelDto,
	TeamMemberDto,
	TeamMessageDto,
	TeamOverviewDto,
	TeamParticipantDto,
	FacebookPageOptionDto,
	SocialChannelDto,
	SocialImageDto,
	SocialMetricDto,
	SocialOverviewDto,
	SocialPostDto,
	EngageAccountDto,
	EngageAccountMemberDto,
	EngageCatalogItemDto,
	WebPushSettingsDto,
	WebPushVapidPublicKeyDto,
	EngageConversationDto,
	EngageLoginDto,
	EngageMessageDto,
	EngageNoteDto,
	EngageOverviewDto,
	SupportCaseDto,
	TotpSetupDto,
} from "./dto.js";

/**
 * Typed client for the Bafe control-plane API.
 *
 * Shared by the control panel, the admin console and the webmail shell so that
 * a change to an endpoint breaks compilation rather than production.
 */

export class ApiError extends Error {
	readonly code: ErrorCode;
	readonly status: number;
	readonly detail?: string;
	readonly requestId?: string;
	readonly fields?: Record<string, string[]>;

	constructor(status: number, body: ApiErrorBody["error"]) {
		super(body.message);
		this.name = "ApiError";
		this.status = status;
		this.code = body.code;
		this.detail = body.detail;
		this.requestId = body.requestId;
		this.fields = body.fields;
	}

	/** True when retrying the same request could plausibly succeed. */
	get retryable(): boolean {
		return (
			this.code === "provider_unavailable" ||
			this.code === "bridge_unavailable" ||
			this.code === "internal" ||
			this.status >= 500
		);
	}
}

export interface ApiClientOptions {
	baseUrl: string;
	fetch?: typeof globalThis.fetch;
	/** Read from the CSRF cookie and echoed back on unsafe methods. */
	getCsrfToken?: () => string | null;
	/** Called on 401 so the app can redirect to sign-in once, centrally. */
	onUnauthenticated?: () => void;
	/** Scopes every tenant request to one organisation. */
	getOrganisationId?: () => string | null;
	/** Which product surface this client belongs to. Cookies are not shared. */
	surface?: AppSurface;
}

const CSRF_HEADER = "x-bafe-csrf";
const ORGANISATION_HEADER = "x-bafe-organisation";

/** Only a dead session should clear cookies and redirect to sign-in. */
function shouldSignOutOnError(status: number, code: ErrorCode): boolean {
	return status === 401 && code === "unauthenticated";
}

/**
 * Empty `baseUrl` means same-origin `/api/...`. `new URL("/api")` throws
 * without a base, so we take the browser origin when one is available.
 */
function resolveRequestUrl(baseUrl: string, path: string): URL {
	if (baseUrl) return new URL(`${baseUrl}${path}`);
	const origin = (globalThis as { location?: { origin?: string } }).location?.origin ?? "http://127.0.0.1";
	return new URL(path, origin);
}

export class ApiClient {
	readonly #baseUrl: string;
	readonly #fetch: typeof globalThis.fetch;
	readonly #getCsrfToken: () => string | null;
	readonly #onUnauthenticated: (() => void) | undefined;
	readonly #getOrganisationId: () => string | null;
	readonly #surface: AppSurface;

	constructor(options: ApiClientOptions) {
		this.#baseUrl = options.baseUrl.replace(/\/$/, "");
		this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
		this.#surface = options.surface ?? "manage";
		this.#getCsrfToken = options.getCsrfToken ?? (() => readCookie(csrfCookieName(this.#surface)));
		this.#onUnauthenticated = options.onUnauthenticated;
		this.#getOrganisationId = options.getOrganisationId ?? (() => null);
	}

	async #request<T>(
		method: string,
		path: string,
		options: {
			body?: unknown;
			raw?: Blob;
			form?: FormData;
			query?: Record<string, string | number | boolean | null | undefined>;
			signal?: AbortSignal;
			blob?: boolean;
		} = {},
	): Promise<T> {
		const url = resolveRequestUrl(this.#baseUrl, path);
		for (const [key, value] of Object.entries(options.query ?? {})) {
			if (value !== null && value !== undefined) url.searchParams.set(key, String(value));
		}

		const headers: Record<string, string> = {
			Accept: options.blob ? "*/*" : "application/json",
			[SURFACE_HEADER]: this.#surface,
		};
		if (options.raw !== undefined) headers["Content-Type"] = "application/octet-stream";
		else if (options.form === undefined && options.body !== undefined) headers["Content-Type"] = "application/json";

		if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
			const csrf = this.#getCsrfToken();
			if (csrf) headers[CSRF_HEADER] = csrf;
		}

		const organisationId = this.#getOrganisationId();
		if (organisationId) headers[ORGANISATION_HEADER] = organisationId;

		let response: Response;
		try {
			response = await this.#fetch(url.toString(), {
				method,
				headers,
				credentials: "include",
				signal: options.signal,
				body:
					options.raw !== undefined
						? options.raw
						: options.form !== undefined
							? options.form
							: options.body === undefined
								? undefined
								: JSON.stringify(options.body),
			});
		} catch (cause) {
			if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
			throw new ApiError(0, {
				code: "provider_unavailable",
				message: "We could not reach Key. Check your connection and try again.",
				requestId: "",
			});
		}

		if (response.status === 204) return undefined as T;

		if (options.blob && response.ok) return (await response.blob()) as T;

		const body = (await response.json().catch(() => null)) as unknown;

		if (!response.ok) {
			const error = (body as ApiErrorBody | null)?.error ?? {
				code: "internal" as ErrorCode,
				message: "Something went wrong. Try again.",
				requestId: response.headers.get("x-request-id") ?? "",
			};
			if (shouldSignOutOnError(response.status, error.code)) this.#onUnauthenticated?.();
			throw new ApiError(response.status, error);
		}

		return (body as ApiEnvelope<T>).data;
	}

	#get<T>(path: string, query?: Record<string, string | number | boolean | null | undefined>, signal?: AbortSignal) {
		return this.#request<T>("GET", path, { query, signal });
	}

	#post<T>(path: string, body?: unknown) {
		return this.#request<T>("POST", path, { body });
	}

	#patch<T>(path: string, body?: unknown) {
		return this.#request<T>("PATCH", path, { body });
	}

	#put<T>(path: string, body?: unknown) {
		return this.#request<T>("PUT", path, { body });
	}

	#delete<T>(path: string, body?: unknown) {
		return this.#request<T>("DELETE", path, { body });
	}

	async #page<T>(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
		const url = resolveRequestUrl(this.#baseUrl, path);
		for (const [key, value] of Object.entries(query ?? {})) {
			if (value !== null && value !== undefined) url.searchParams.set(key, String(value));
		}
		// Paged endpoints return meta alongside data, so the envelope is kept.
		const headers: Record<string, string> = { Accept: "application/json", [SURFACE_HEADER]: this.#surface };
		const organisationId = this.#getOrganisationId();
		if (organisationId) headers[ORGANISATION_HEADER] = organisationId;

		const response = await this.#fetch(url.toString(), { headers, credentials: "include" });
		const body = (await response.json().catch(() => null)) as PageEnvelope<T> | ApiErrorBody | null;

		if (!response.ok) {
			const error = (body as ApiErrorBody | null)?.error ?? {
				code: "internal" as ErrorCode,
				message: "Something went wrong. Try again.",
				requestId: "",
			};
			if (shouldSignOutOnError(response.status, error.code)) this.#onUnauthenticated?.();
			throw new ApiError(response.status, error);
		}
		return body as PageEnvelope<T>;
	}

	// --- Authentication ---------------------------------------------------

	register(input: { fullName: string; email: string; password: string; organisationName: string; country?: string; timezone?: string }) {
		return this.#post<SessionDto>("/api/v1/auth/register", input);
	}

	login(input: { email: string; password: string; totpCode?: string; recoveryCode?: string }) {
		return this.#post<SessionDto>("/api/v1/auth/login", input);
	}

	logout() {
		return this.#post<void>("/api/v1/auth/logout");
	}

	getSession(signal?: AbortSignal) {
		return this.#get<SessionDto>("/api/v1/auth/session", undefined, signal);
	}

	verifyEmail(token: string) {
		return this.#post<void>("/api/v1/auth/verify-email", { token });
	}

	resendVerification() {
		return this.#post<void>("/api/v1/auth/verify-email/resend");
	}

	requestPasswordReset(email: string) {
		return this.#post<void>("/api/v1/auth/password-reset", { email });
	}

	confirmPasswordReset(input: { token: string; password: string }) {
		return this.#post<void>("/api/v1/auth/password-reset/confirm", input);
	}

	previewInvitation(token: string) {
		return this.#get<InvitationPreviewDto>("/api/v1/auth/invitations/preview", { token });
	}

	acceptInvitation(input: { token: string; fullName?: string; password?: string }) {
		return this.#post<SessionDto>("/api/v1/auth/invitations/accept", input);
	}

	reauthenticate(password: string) {
		return this.#post<void>("/api/v1/auth/reauthenticate", { password });
	}

	changePassword(input: { currentPassword: string; newPassword: string }) {
		return this.#post<void>("/api/v1/auth/change-password", input);
	}

	beginTotpSetup() {
		return this.#post<TotpSetupDto>("/api/v1/auth/2fa/setup");
	}

	confirmTotpSetup(code: string) {
		return this.#post<RecoveryCodesDto>("/api/v1/auth/2fa/confirm", { code });
	}

	disableTotp(password: string) {
		return this.#post<void>("/api/v1/auth/2fa/disable", { password });
	}

	regenerateRecoveryCodes() {
		return this.#post<RecoveryCodesDto>("/api/v1/auth/2fa/recovery-codes");
	}

	listSessions() {
		return this.#get<SessionDeviceDto[]>("/api/v1/auth/sessions");
	}

	revokeSession(id: string) {
		return this.#delete<void>(`/api/v1/auth/sessions/${id}`);
	}

	revokeOtherSessions() {
		return this.#post<void>("/api/v1/auth/sessions/revoke-others");
	}

	/** Exchanges the control-panel session for a short-lived webmail session. */
	createWebmailHandoff(mailboxId: string) {
		return this.#post<{ url: string; stalwartAccountId: string }>("/api/v1/auth/webmail-handoff", { mailboxId });
	}

	selectWorkspace(organisationId: string) {
		return this.#post<{ organisationId: string; organisationSlug: string; apps: WorkspaceApp[] }>(
			"/api/v1/workspaces/select",
			{ organisationId },
		);
	}

	resolveWorkspaceSlug(slug: string) {
		return this.#get<{ organisationId: string; slug: string }>(`/api/v1/workspaces/slug/${encodeURIComponent(slug)}`);
	}

	createWorkspace(name: string) {
		return this.#post<{ id: string; name: string; apps: WorkspaceApp[] }>(
			"/api/v1/workspaces",
			{ name },
		);
	}

	getWorkspaceApps() {
		return this.#get<{ apps: WorkspaceApp[] }>("/api/v1/workspaces/apps");
	}

	setWorkspaceApps(apps: WorkspaceApp[]) {
		return this.#put<{ apps: WorkspaceApp[] }>("/api/v1/workspaces/apps", { apps });
	}

	leaveWorkspace(organisationId: string) {
		return this.#post<void>("/api/v1/workspaces/leave", { organisationId });
	}

	closeWorkspace(organisationId: string, confirmOrganisationName: string) {
		return this.#post<void>("/api/v1/workspaces/close", { organisationId, confirmOrganisationName });
	}

	setMemberApps(userId: string, apps: WorkspaceApp[]) {
		return this.#put<{ userId: string; apps: WorkspaceApp[] }>(
			`/api/v1/workspaces/members/${userId}/apps`,
			{ apps },
		);
	}

	getWorkspaceConfig() {
		return this.#get<WorkspaceConfigDto>("/api/v1/workspaces/config");
	}

	getTeamOverview() {
		return this.#get<TeamOverviewDto>("/api/v1/team/overview");
	}

	listTeamChannels() {
		return this.#get<TeamChannelDto[]>("/api/v1/team/channels");
	}

	createTeamChannel(name: string) {
		return this.#post<{ id: string }>("/api/v1/team/channels", { name });
	}

	createTeamDirect(userId: string) {
		return this.#post<{ id: string }>("/api/v1/team/direct", { userId });
	}

	listTeamMembers(channelId: string) {
		return this.#get<TeamMemberDto[]>(`/api/v1/team/channels/${channelId}/members`);
	}

	inviteTeamMember(channelId: string, userId: string) {
		return this.#post<void>(`/api/v1/team/channels/${channelId}/members`, { userId });
	}

	listTeamMessages(channelId: string) {
		return this.#get<TeamMessageDto[]>(`/api/v1/team/channels/${channelId}/messages`);
	}

	sendTeamMessage(channelId: string, body: string) {
		return this.#post<TeamMessageDto>(`/api/v1/team/channels/${channelId}/messages`, { body });
	}

	editTeamMessage(channelId: string, messageId: string, body: string) {
		return this.#patch<TeamMessageDto>(`/api/v1/team/channels/${channelId}/messages/${messageId}`, { body });
	}

	deleteTeamMessage(channelId: string, messageId: string) {
		return this.#delete<TeamMessageDto>(`/api/v1/team/channels/${channelId}/messages/${messageId}`);
	}

	sendTeamMedia(channelId: string, file: Blob, caption: string, albumId?: string) {
		const form = new FormData();
		form.append("file", file);
		form.append("caption", caption);
		if (albumId) form.append("albumId", albumId);
		return this.#request<TeamMessageDto>("POST", `/api/v1/team/channels/${channelId}/media`, { form });
	}

	readTeamMedia(channelId: string, messageId: string) {
		return this.#request<Blob>("GET", `/api/v1/team/channels/${channelId}/messages/${messageId}/media`, {
			blob: true,
		});
	}

	markTeamRead(channelId: string) {
		return this.#post<void>(`/api/v1/team/channels/${channelId}/read`);
	}

	deleteTeamChannel(channelId: string) {
		return this.#delete<void>(`/api/v1/team/channels/${channelId}`);
	}

	listTeamParticipants() {
		return this.#get<TeamParticipantDto[]>("/api/v1/team/participants");
	}

	inviteTeamParticipant(email: string) {
		return this.#post<{ id: string; email: string; status: "invited" | "active" }>(
			"/api/v1/team/participants",
			{ email },
		);
	}

	removeTeamParticipant(participantId: string) {
		return this.#delete<void>(`/api/v1/team/participants/${participantId}`);
	}

	getSocialOverview() {
		return this.#get<SocialOverviewDto>("/api/v1/social/overview");
	}

	listSocialChannels() {
		return this.#get<SocialChannelDto[]>("/api/v1/social/channels");
	}

	connectSocialChannel(network: SocialNetwork, handle?: string) {
		return this.#post<{ id?: string; url?: string }>("/api/v1/social/channels", { network, handle });
	}

	claimSocialChannels() {
		return this.#post<SocialChannelDto[]>("/api/v1/social/channels/claim");
	}

	listFacebookPages(selectionId: string) {
		return this.#get<FacebookPageOptionDto[]>("/api/v1/social/facebook/pages", { selection: selectionId });
	}

	confirmFacebookPages(selectionId: string, pageIds: string[]) {
		return this.#post<SocialChannelDto[]>("/api/v1/social/facebook/pages", { selectionId, pageIds });
	}

	disconnectSocialChannel(channelId: string) {
		return this.#delete<{ id: string }>(`/api/v1/social/channels/${channelId}`);
	}

	socialAnalytics(channelId: string, days = 30) {
		return this.#get<SocialMetricDto[]>(`/api/v1/social/channels/${channelId}/analytics`, { days });
	}

	uploadSocialMedia(file: Blob) {
		const form = new FormData();
		form.append("file", file);
		return this.#request<SocialImageDto>("POST", "/api/v1/social/media", { form });
	}

	listSocialPosts(start?: string, end?: string) {
		return this.#get<SocialPostDto[]>("/api/v1/social/posts", { start, end });
	}

	createSocialPost(input: {
		body: string;
		channelIds: string[];
		scheduledAt?: string | null;
		mode?: "now" | "schedule" | "draft";
		images?: SocialImageDto[];
	}) {
		return this.#post<SocialPostDto>("/api/v1/social/posts", input);
	}

	deleteSocialPost(postId: string) {
		return this.#delete<{ id: string }>(`/api/v1/social/posts/${postId}`);
	}

	getEngageOverview() {
		return this.#get<EngageOverviewDto>("/api/v1/engage/overview");
	}

	listEngageCatalog() {
		return this.#get<EngageCatalogItemDto[]>("/api/v1/engage/catalog");
	}

	listEngageAccounts() {
		return this.#get<EngageAccountDto[]>("/api/v1/engage/accounts");
	}

	connectEngageAccount(input: {
		network: EngageInboxNetwork;
		displayName: string;
		providerAccountId: string;
		credential?: string;
	}) {
		return this.#post<{ id: string }>("/api/v1/engage/accounts", input);
	}

	startEngageLogin(
		network: EngageInboxNetwork,
		input?: { nickname?: string; consentAcknowledged?: boolean; method?: string; phone?: string; email?: string },
	) {
		return this.#post<EngageLoginDto>("/api/v1/engage/logins", { network, ...input });
	}

	getEngageLogin(id: string) {
		return this.#get<EngageLoginDto>(`/api/v1/engage/logins/${id}`);
	}

	completeEngageLogin(id: string) {
		return this.#post<{ id: string; login: EngageLoginDto }>(`/api/v1/engage/logins/${id}/complete`, {});
	}

	cancelEngageLogin(id: string) {
		return this.#post<void>(`/api/v1/engage/logins/${id}/cancel`, {});
	}

	refreshEngageLogin(id: string) {
		return this.#post<EngageLoginDto>(`/api/v1/engage/logins/${id}/refresh`, {});
	}

	submitEngageLoginSecret(id: string, input: string | { password?: string; phone?: string; code?: string }) {
		const body = typeof input === "string" ? { password: input } : input;
		return this.#post<EngageLoginDto>(`/api/v1/engage/logins/${id}/secret`, body);
	}

	getEngageLoginChallenge(id: string) {
		return this.#request<Blob>("GET", `/api/v1/engage/logins/${id}/challenge`, { blob: true });
	}

	clickEngageLoginChallenge(id: string, input: { x: number; y: number }) {
		return this.#post<EngageLoginDto>(`/api/v1/engage/logins/${id}/challenge`, input);
	}

	interactEngageLoginChallenge(
		id: string,
		input:
			| { kind: "click"; x: number; y: number }
			| { kind: "type"; text: string }
			| { kind: "key"; key: string }
			| { kind: "scroll"; deltaX: number; deltaY: number },
	) {
		return this.#post<EngageLoginDto>(`/api/v1/engage/logins/${id}/challenge/interact`, input);
	}

	disconnectEngageAccount(id: string) {
		return this.#delete<void>(`/api/v1/engage/accounts/${id}`);
	}

	listEngageAccountMembers(accountId: string) {
		return this.#get<EngageAccountMemberDto[]>(`/api/v1/engage/accounts/${accountId}/members`);
	}

	inviteEngageAccountMember(accountId: string, userId: string, role: "admin" | "agent") {
		return this.#post<{ userId: string; role: string }>(`/api/v1/engage/accounts/${accountId}/members`, {
			userId,
			role,
		});
	}

	removeEngageAccountMember(accountId: string, userId: string) {
		return this.#delete<void>(`/api/v1/engage/accounts/${accountId}/members/${userId}`);
	}

	listEngageConversations() {
		return this.#get<EngageConversationDto[]>("/api/v1/engage/conversations");
	}

	deleteEngageConversation(conversationId: string) {
		return this.#delete<void>(`/api/v1/engage/conversations/${conversationId}`);
	}

	listEngageMessages(conversationId: string) {
		return this.#get<EngageMessageDto[]>(`/api/v1/engage/conversations/${conversationId}/messages`);
	}

	assignEngageConversation(conversationId: string, assigneeId: string | null) {
		return this.#post<void>(`/api/v1/engage/conversations/${conversationId}/assign`, { assigneeId });
	}

	pinEngageConversation(conversationId: string, pinned: boolean) {
		return this.#post<void>(`/api/v1/engage/conversations/${conversationId}/pin`, { pinned });
	}

	replyEngageConversation(
		conversationId: string,
		body: string,
		idempotencyKey?: string,
		image?: { mimeType: string; bytesBase64: string; filename?: string },
		replyToMessageId?: string,
		replyToName?: string,
		replyToBody?: string,
	) {
		return this.#post<EngageMessageDto>(`/api/v1/engage/conversations/${conversationId}/reply`, {
			body,
			idempotencyKey,
			image,
			replyToMessageId,
			replyToName,
			replyToBody,
		});
	}

	editEngageMessage(conversationId: string, messageId: string, body: string) {
		return this.#patch<EngageMessageDto>(`/api/v1/engage/conversations/${conversationId}/messages/${messageId}`, {
			body,
		});
	}

	deleteEngageMessage(conversationId: string, messageId: string) {
		return this.#delete<EngageMessageDto>(`/api/v1/engage/conversations/${conversationId}/messages/${messageId}`);
	}

	readEngageMedia(conversationId: string, messageId: string) {
		return this.#request<Blob>("GET", `/api/v1/engage/conversations/${conversationId}/messages/${messageId}/media`, {
			blob: true,
		});
	}

	listEngageNotes(conversationId: string) {
		return this.#get<EngageNoteDto[]>(`/api/v1/engage/conversations/${conversationId}/notes`);
	}

	createEngageNote(conversationId: string, body: string) {
		return this.#post<EngageNoteDto>(`/api/v1/engage/conversations/${conversationId}/notes`, { body });
	}

	markEngageConversationRead(conversationId: string) {
		return this.#post<void>(`/api/v1/engage/conversations/${conversationId}/read`, {});
	}

	getHubRecords(mine = false) {
		return this.#get<HubRecordsDto>("/api/v1/hub/records", mine ? { mine: "1" } : undefined);
	}

	createHubLead(input: {
		source: string;
		site: string;
		name: string;
		phone: string;
		email: string;
		location: string;
		service: string;
		page?: string;
	}) {
		return this.#post<{ id: number }>("/api/v1/hub/leads", input);
	}

	updateHubLead(id: string, input: {
		source: string;
		site: string;
		name: string;
		phone: string;
		email: string;
		location: string;
		service: string;
		page?: string;
	}) {
		return this.#patch<{ id: number }>(`/api/v1/hub/leads/${id}`, input);
	}

	deleteHubLead(id: string) {
		return this.#delete<void>(`/api/v1/hub/leads/${id}`);
	}

	createHubTask(input: {
		title: string;
		description?: string;
		assignee?: string;
		assignees?: string[];
		assigneeUserIds?: string[];
		dueOn?: string;
		tag?: string;
		tags?: string[];
		status?: string;
		checklist?: HubTaskChecklistItem[];
		checklistTitle?: string;
		attachments?: HubTaskAttachmentItem[];
		boardPosition?: number;
	}) {
		return this.#post<{ id: number }>("/api/v1/hub/tasks", input);
	}

	updateHubTask(id: string, input: {
		title?: string;
		description?: string;
		assignee?: string;
		assignees?: string[];
		assigneeUserIds?: string[];
		dueOn?: string;
		tag?: string;
		tags?: string[];
		status?: string;
		checklist?: HubTaskChecklistItem[];
		checklistTitle?: string;
		attachments?: HubTaskAttachmentItem[];
		boardPosition?: number;
	}) {
		return this.#patch<{ id: number }>(`/api/v1/hub/tasks/${encodeURIComponent(id)}`, input);
	}

	deleteHubTask(id: string) {
		return this.#delete<void>(`/api/v1/hub/tasks/${encodeURIComponent(id)}`);
	}

	createHubTaskComment(taskId: string, input: { body: string }) {
		return this.#post<HubTaskCommentDto>(
			`/api/v1/hub/tasks/${encodeURIComponent(taskId)}/comments`,
			input,
		);
	}

	updateHubTaskComment(taskId: string, commentId: string, input: { body: string }) {
		return this.#patch<HubTaskCommentDto>(
			`/api/v1/hub/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
			input,
		);
	}

	deleteHubTaskComment(taskId: string, commentId: string) {
		return this.#delete<void>(
			`/api/v1/hub/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
		);
	}

	deleteHubTag(name: string) {
		return this.#delete<void>("/api/v1/hub/tags", { name });
	}

	updateHubTag(input: { name: string; color: string }) {
		return this.#patch<void>("/api/v1/hub/tags", input);
	}

	updateHubAssigneeColor(input: { username: string; color: string }) {
		return this.#patch<void>("/api/v1/hub/assignee-colors", input);
	}

	updateHubInventoryColumnPrefs(input: { columns: Record<string, boolean> }) {
		return this.#patch<void>("/api/v1/hub/inventory-ui", input);
	}

	updateHubPeopleSettings(input: {
		leavePolicy?: Array<{ kind: string; days: number; paid: boolean; approval: boolean }>;
		claimPolicy?: Array<{ kind: string; limit: string; approval: boolean }>;
		attendanceSettings?: { lateMinutes: number; shifts: Array<{ id: string; name: string; start: string; end: string }> };
		payrollSettings?: { payDay: number; epf: number; socso: string; eis: string };
		attendanceSlots?: Array<{ id?: number; mine?: boolean; person: string; days: string[]; shift: string }>;
	}) {
		return this.#patch<void>("/api/v1/hub/people-settings", input);
	}

	createHubExpense(input: { spentOn: string; description: string; category: string; amount: string; payee?: string }) {
		return this.#post<{ id: number }>("/api/v1/hub/expenses", input);
	}

	appendHubBook(input: { list: string; record: Record<string, unknown> }) {
		return this.#post<{ id: number | string }>("/api/v1/hub/books", input);
	}

	updateHubBook(input: { list: string; id: number | string; record: Record<string, unknown> }) {
		return this.#patch<{ id: number }>("/api/v1/hub/books", input);
	}

	deleteHubBook(input: { list: string; id: number | string }) {
		return this.#delete<void>("/api/v1/hub/books", input);
	}

	reorderHubBook(input: { list: string; ids: number[] }) {
		return this.#post<{ count: number }>("/api/v1/hub/books/order", input);
	}

	getSiteRecords() {
		return this.#get<{ sites: WorkspaceSiteDto[] }>("/api/v1/site/records");
	}

	listHostedSites() {
		return this.#get<{ sites: HostedSiteDto[] }>("/api/v1/site/hosted");
	}

	listSiteTags() {
		return this.#get<{ tags: SiteTagDto[] }>("/api/v1/site/tags");
	}

	createSiteTag(name: string) {
		return this.#post<SiteTagDto>("/api/v1/site/tags", { name });
	}

	deleteSiteTag(id: string) {
		return this.#delete<void>(`/api/v1/site/tags/${id}`);
	}

	setHostedSiteTags(id: string, tagIds: string[]) {
		return this.#put<{ tags: SiteTagDto[] }>(`/api/v1/site/hosted/${id}/tags`, { tagIds });
	}

	listSiteFiles(id: string, query: { path?: string; q?: string } = {}) {
		return this.#get<SiteFileListDto>(`/api/v1/site/hosted/${id}/files`, query);
	}

	readSiteFile(id: string, path: string) {
		return this.#get<SiteTextFileDto>(`/api/v1/site/hosted/${id}/file`, { path });
	}

	writeSiteFile(id: string, path: string, content: string, expected: { mtimeNs: string; size: number }) {
		return this.#patch<SiteFileWriteDto>(`/api/v1/site/hosted/${id}/file`, {
			path,
			content,
			expectedMtimeNs: expected.mtimeNs,
			expectedSize: expected.size,
		});
	}

	uploadSiteFile(
		id: string,
		input: { path: string; filename: string; file: Blob; conflict?: "keep" | "replace" },
	) {
		return this.#request<SiteUploadResultDto>("POST", `/api/v1/site/hosted/${id}/files`, {
			raw: input.file,
			query: { path: input.path, filename: input.filename, conflict: input.conflict ?? "keep" },
		});
	}

	createSiteEntry(id: string, input: { path: string; name: string; kind: "file" | "directory"; content?: string }) {
		return this.#post<{ path: string }>(`/api/v1/site/hosted/${id}/entries`, input);
	}

	deleteSiteFile(id: string, path: string) {
		return this.#delete<void>(`/api/v1/site/hosted/${id}/files`, { path, confirm: true });
	}

	updateHostedSite(id: string, input: { wwwMode?: "none" | "to_apex" | "to_www"; enabled?: boolean }) {
		return this.#patch<HostedSiteDto>(`/api/v1/site/hosted/${id}`, input);
	}

	listSiteRedirects() {
		return this.#get<{ redirects: SiteRedirectDto[] }>("/api/v1/site/redirects");
	}

	createSiteRedirect(input: { hostedSiteId: string; sourceHost: string; destination: string; sourcePath?: string }) {
		const sourcePath = input.sourcePath?.trim() ?? "";
		return this.#post<SiteRedirectDto>("/api/v1/site/redirects", {
			hostedSiteId: input.hostedSiteId,
			kind: sourcePath === "" ? "host" : "path",
			sourceHost: input.sourceHost,
			sourcePath,
			destination: input.destination,
			statusCode: 301,
		});
	}

	updateSiteRedirect(id: string, input: { destination?: string; sourcePath?: string; enabled?: boolean }) {
		return this.#patch<SiteRedirectDto>(`/api/v1/site/redirects/${id}`, input);
	}

	deleteSiteRedirect(id: string) {
		return this.#delete<void>(`/api/v1/site/redirects/${id}`);
	}

	listCustomerDatabases() {
		return this.#get<CustomerDatabaseListDto>("/api/v1/site/databases");
	}

	createCustomerDatabase(name: string) {
		return this.#post<CustomerDatabaseDto>("/api/v1/site/databases", { name });
	}

	deleteCustomerDatabase(name: string, confirm: string) {
		return this.#delete<{ name: string; deleted: true }>(`/api/v1/site/databases/${encodeURIComponent(name)}`, { confirm });
	}

	createCustomerDatabaseRole(input: { name: string; password: string; passwordConfirm: string }) {
		return this.#post<{ name: string }>("/api/v1/site/database-roles", input);
	}

	updateCustomerDatabaseRole(name: string, input: { password: string; passwordConfirm: string }) {
		return this.#patch<{ name: string }>(`/api/v1/site/database-roles/${encodeURIComponent(name)}`, input);
	}

	deleteCustomerDatabaseRole(name: string, confirm: string) {
		return this.#delete<{ name: string; deleted: true }>(`/api/v1/site/database-roles/${encodeURIComponent(name)}`, {
			confirm,
		});
	}

	grantCustomerDatabasePrivileges(name: string, input: { role: string; privileges: string[] }) {
		return this.#post<{ database: string; role: string; privileges: string[] }>(
			`/api/v1/site/databases/${encodeURIComponent(name)}/privileges`,
			input,
		);
	}

	revokeCustomerDatabasePrivileges(name: string, input: { role: string; privileges: string[] }) {
		return this.#delete<{ database: string; role: string; privileges: string[] }>(
			`/api/v1/site/databases/${encodeURIComponent(name)}/privileges`,
			input,
		);
	}

	updateWorkspaceConfig(input: Partial<Omit<WorkspaceConfigDto, "apps" | "mailClient">>) {
		return this.#patch<WorkspaceConfigDto>("/api/v1/workspaces/config", input);
	}

	// --- Organisation -----------------------------------------------------

	getOrganisation() {
		return this.#get<OrganisationDto>("/api/v1/organisation");
	}

	updateOrganisation(input: Partial<Pick<OrganisationDto, "name" | "legalName" | "registrationNumber" | "billingEmail" | "country" | "timezone">>) {
		return this.#patch<OrganisationDto>("/api/v1/organisation", input);
	}

	listMembers() {
		return this.#get<MemberDto[]>("/api/v1/organisation/members");
	}

	inviteMember(input: { email: string; role: string }) {
		return this.#post<MemberDto>("/api/v1/organisation/members", input);
	}

	changeMemberRole(userId: string, role: string) {
		return this.#patch<MemberDto>(`/api/v1/organisation/members/${userId}`, { role });
	}

	setMemberHubTaskAccess(userId: string, viewAllHubTasks: boolean) {
		return this.#patch<MemberDto>(`/api/v1/organisation/members/${userId}/hub-tasks`, { viewAllHubTasks });
	}

	removeMember(userId: string) {
		return this.#delete<void>(`/api/v1/organisation/members/${userId}`);
	}

	cancelInvitation(invitationId: string) {
		return this.#delete<void>(`/api/v1/organisation/invitations/${invitationId}`);
	}

	provisionInvitation(invitationId: string, input: { password?: string } = {}) {
		return this.#post<MemberDto>(
			`/api/v1/organisation/invitations/${invitationId}/provision`,
			input,
		);
	}

	transferOwnership(input: { userId: string; confirmOrganisationName: string }) {
		return this.#post<void>("/api/v1/organisation/transfer-ownership", input);
	}

	// --- Overview and usage -----------------------------------------------

	getOverview() {
		return this.#get<OverviewDto>("/api/v1/overview");
	}

	getStorage() {
		return this.#get<StorageDto>("/api/v1/usage/storage");
	}

	// --- Domains ----------------------------------------------------------

	listDomains() {
		return this.#get<DomainDto[]>("/api/v1/domains");
	}

	getDomain(id: string) {
		return this.#get<DomainDetailDto>(`/api/v1/domains/${id}`);
	}

	createDomain(
		domain: string,
		input: {
			email?: boolean;
			sites?: boolean;
			platform?: "hub" | "webmail";
			includeWww?: boolean;
		} = {},
	) {
		return this.#post<DomainDetailDto>("/api/v1/domains", { domain, ...input });
	}

	updateDomainCapabilities(
		id: string,
		input: { email: boolean; sites: boolean; includeWww?: boolean },
	) {
		return this.#patch<DomainDetailDto>(`/api/v1/domains/${id}/capabilities`, input);
	}

	checkDomainDns(id: string) {
		return this.#post<DomainDetailDto>(`/api/v1/domains/${id}/check-dns`);
	}

	removeDomain(id: string, confirmDomain: string) {
		return this.#delete<void>(`/api/v1/domains/${id}`, { confirmDomain });
	}

	previewCloudflareRecords(id: string) {
		return this.#post<CloudflarePreviewDto>(`/api/v1/domains/${id}/cloudflare/preview`);
	}

	applyCloudflareRecords(id: string, input: { zoneId: string; approvedRecordIds: string[] }) {
		return this.#post<DomainDetailDto>(`/api/v1/domains/${id}/cloudflare/apply`, input);
	}

	// --- Mailboxes --------------------------------------------------------

	listMailboxes(query?: { domainId?: string; cursor?: string | null; limit?: number }) {
		return this.#page<MailboxDto>("/api/v1/mailboxes", query);
	}

	/** Inboxes this person was granted. Membership alone is not included. */
	listAccessibleMailboxes() {
		return this.#get<AccessibleMailboxDto[]>("/api/v1/mailboxes/accessible");
	}

	listInboxCounters() {
		return this.#get<{ items: MailInboxCounterDto[] }>("/api/v1/mailboxes/inbox-counters");
	}

	fetchUnifiedThreadList(body: {
		segment: string;
		mailboxIds: string[];
		limitPerMailbox?: number;
		cursor?: string | null;
	}) {
		return this.#post<UnifiedThreadListDto>("/api/v1/mail/unified-threads", body);
	}

	/** Every grant for webmail (sidebar flag ignored). */
	listGrantedMailboxes() {
		return this.#get<AccessibleMailboxDto[]>("/api/v1/mailboxes/granted");
	}

	listMailboxGrants(mailboxId: string) {
		return this.#get<MailboxGrantDto[]>(`/api/v1/mailboxes/${mailboxId}/grants`);
	}

	assignMailboxAccess(
		mailboxId: string,
		input: { userId: string; access: "full" | "read_only"; showInSidebar?: boolean },
	) {
		return this.#post<MailboxGrantDto>(`/api/v1/mailboxes/${mailboxId}/grants`, input);
	}

	updateMailboxGrantSidebar(mailboxId: string, userId: string, showInSidebar: boolean) {
		return this.#request<MailboxGrantDto>("PATCH", `/api/v1/mailboxes/${mailboxId}/grants/${userId}`, {
			body: { showInSidebar },
		});
	}

	removeMailboxAccess(mailboxId: string, userId: string) {
		return this.#delete<void>(`/api/v1/mailboxes/${mailboxId}/grants/${userId}`);
	}

	recordMailboxOpens(mailboxId: string, messageIds: string[]) {
		return this.#post<MessageOpenDto[]>(`/api/v1/mailboxes/${mailboxId}/opens`, { messageIds });
	}

	getMailboxOpens(mailboxId: string, messageIds: string[]) {
		return this.#get<MessageOpenDto[]>(`/api/v1/mailboxes/${encodeURIComponent(mailboxId)}/opens`, {
			messageIds: messageIds.join(","),
		});
	}

	recordUserMessageReads(mailboxId: string, messageIds: string[]) {
		return this.#post<UserMessageReadDto[]>(`/api/v1/mailboxes/${mailboxId}/message-reads`, { messageIds });
	}

	getUserMessageReads(mailboxId: string, messageIds: string[]) {
		return this.#get<UserMessageReadDto[]>(`/api/v1/mailboxes/${encodeURIComponent(mailboxId)}/message-reads`, {
			messageIds: messageIds.join(","),
		});
	}

	/** Sidebar groups for this sign-in. Another person does not receive them. */
	listSidebarGroups() {
		return this.#get<SidebarGroupDto[]>("/api/v1/mailboxes/sidebar-groups");
	}

	saveSidebarGroups(groups: SidebarGroupDto[]) {
		return this.#request<SidebarGroupDto[]>("PUT", "/api/v1/mailboxes/sidebar-groups", { body: { groups } });
	}

	deleteAllSidebarGroups() {
		return this.#request<SidebarGroupDto[]>("DELETE", "/api/v1/mailboxes/sidebar-groups");
	}

	getSidebarAccountOrder() {
		return this.#get<SidebarAccountOrderDto>("/api/v1/mailboxes/sidebar-account-order");
	}

	saveSidebarAccountOrder(mailboxIds: string[]) {
		return this.#request<SidebarAccountOrderDto>("PUT", "/api/v1/mailboxes/sidebar-account-order", {
			body: { mailboxIds },
		});
	}

	getInboxGrouping() {
		return this.#get<InboxGroupingDto>("/api/v1/mailboxes/inbox-grouping");
	}

	saveInboxGrouping(input: InboxGroupingDto) {
		return this.#request<InboxGroupingDto>("PUT", "/api/v1/mailboxes/inbox-grouping", { body: input });
	}

	getMailSignatures() {
		return this.#get<MailSignaturesDto>("/api/v1/mailboxes/mail-signatures");
	}

	saveMailSignatures(input: MailSignaturesDto) {
		return this.#request<MailSignaturesDto>("PUT", "/api/v1/mailboxes/mail-signatures", { body: input });
	}

	listMailContacts(query?: string) {
		return this.#get<MailContactDto[]>("/api/v1/mailboxes/mail-contacts", query ? { q: query } : undefined);
	}

	createMailContact(input: {
		email: string;
		fullName?: string;
		company?: string | null;
		jobTitle?: string | null;
		displayAs: string;
	}) {
		return this.#post<MailContactDto>("/api/v1/mailboxes/mail-contacts", input);
	}

	updateMailContact(
		contactId: string,
		input: {
			email: string;
			fullName?: string;
			company?: string | null;
			jobTitle?: string | null;
			displayAs: string;
		},
	) {
		return this.#request<MailContactDto>("PATCH", `/api/v1/mailboxes/mail-contacts/${contactId}`, { body: input });
	}

	deleteMailContact(contactId: string) {
		return this.#delete<void>(`/api/v1/mailboxes/mail-contacts/${contactId}`);
	}

	getMailClientSettings(mailboxId: string) {
		return this.#get<MailClientSettingsDto>(`/api/v1/mailboxes/client-settings/${encodeURIComponent(mailboxId)}`);
	}

	testMailClientSettings(mailboxId: string) {
		return this.#post<MailClientSettingsTestDto>(
			`/api/v1/mailboxes/client-settings/${encodeURIComponent(mailboxId)}/test`,
			{},
		);
	}

	getMailbox(id: string) {
		return this.#get<MailboxDetailDto>(`/api/v1/mailboxes/${id}`);
	}

	createMailbox(input: {
		domainId: string;
		localPart: string;
		firstName?: string;
		lastName?: string;
		password: string;
		quotaBytes?: number | null;
	}) {
		return this.#post<MailboxSecretDto>("/api/v1/mailboxes", input);
	}

	updateMailbox(id: string, input: { displayName?: string; quotaBytes?: number | null }) {
		return this.#patch<MailboxDto>(`/api/v1/mailboxes/${id}`, input);
	}

	resetMailboxPassword(id: string, password: string) {
		return this.#post<MailboxSecretDto>(`/api/v1/mailboxes/${id}/reset-password`, { password });
	}

	suspendMailbox(id: string) {
		return this.#post<MailboxDto>(`/api/v1/mailboxes/${id}/suspend`);
	}

	resumeMailbox(id: string) {
		return this.#post<MailboxDto>(`/api/v1/mailboxes/${id}/resume`);
	}

	deleteMailbox(id: string, confirmEmailAddress: string) {
		return this.#delete<void>(`/api/v1/mailboxes/${id}`, { confirmEmailAddress });
	}

	// --- Aliases ----------------------------------------------------------

	listAliases(query?: { domainId?: string }) {
		return this.#get<AliasDto[]>("/api/v1/aliases", query);
	}

	createAlias(input: {
		domainId: string;
		localPart: string;
		destinationType: string;
		mailboxId?: string | null;
		externalAddress?: string | null;
	}) {
		return this.#post<AliasDto>("/api/v1/aliases", input);
	}

	deleteAlias(id: string) {
		return this.#delete<void>(`/api/v1/aliases/${id}`);
	}

	// --- Migration --------------------------------------------------------

	listMigrations() {
		return this.#get<MigrationDto[]>("/api/v1/migrations");
	}

	testMigrationSource(input: {
		sourceHost: string;
		sourcePort: number;
		sourceSecurity: string;
		sourceUsername: string;
		sourcePassword: string;
	}) {
		return this.#post<MigrationTestResultDto>("/api/v1/migrations/test", input);
	}

	createMigration(input: {
		mailboxId: string;
		sourceHost: string;
		sourcePort: number;
		sourceSecurity: string;
		sourceUsername: string;
		sourcePassword: string;
	}) {
		return this.#post<MigrationDto>("/api/v1/migrations", input);
	}

	startMigration(id: string) {
		return this.#post<MigrationDto>(`/api/v1/migrations/${id}/start`);
	}

	pauseMigration(id: string) {
		return this.#post<MigrationDto>(`/api/v1/migrations/${id}/pause`);
	}

	updateMigration(
		id: string,
		input: {
			sourceHost?: string;
			sourcePort?: number;
			sourceSecurity?: string;
			sourceUsername?: string;
			sourcePassword?: string;
		},
	) {
		return this.#patch<MigrationDto>(`/api/v1/migrations/${id}`, input);
	}

	deleteMigration(id: string) {
		return this.#delete<void>(`/api/v1/migrations/${id}`);
	}

	cancelMigration(id: string) {
		return this.#post<MigrationDto>(`/api/v1/migrations/${id}/cancel`);
	}

	// --- Billing ----------------------------------------------------------

	listPlans() {
		return this.#get<PlanDto[]>("/api/v1/plans");
	}

	getBilling() {
		return this.#get<{ subscription: SubscriptionDto | null; storage: StorageDto; invoices: InvoiceDto[] }>(
			"/api/v1/billing",
		);
	}

	createCheckout(input: { planCode: string; billingPeriod?: "monthly" | "annual" }) {
		return this.#post<{ url: string }>("/api/v1/billing/checkout", input);
	}

	createBillingPortal() {
		return this.#post<{ url: string }>("/api/v1/billing/portal");
	}

	changeStoragePlan(planCode: string) {
		return this.#post<SubscriptionDto>("/api/v1/billing/change-storage-plan", { planCode });
	}

	cancelRenewal() {
		return this.#post<SubscriptionDto>("/api/v1/billing/cancel-renewal");
	}

	reactivate() {
		return this.#post<SubscriptionDto>("/api/v1/billing/reactivate");
	}

	// --- Support ----------------------------------------------------------

	listSupportCases() {
		return this.#get<SupportCaseDto[]>("/api/v1/support/cases");
	}

	createSupportCase(input: {
		category: string;
		priority?: string;
		subject: string;
		body: string;
		context?: { domainId?: string; mailboxId?: string; migrationId?: string; traceId?: string };
	}) {
		return this.#post<SupportCaseDto>("/api/v1/support/cases", input);
	}

	// --- Admin ------------------------------------------------------------

	adminOverview() {
		return this.#get<AdminOverviewDto>("/api/v1/admin/overview");
	}

	adminSearchCustomers(query: string, page = 1) {
		return this.#page<AdminCustomerDto>("/api/v1/admin/customers", {
			q: query.trim() || undefined,
			page,
		});
	}

	adminSearchUsers(query: string, page = 1, pageSize = 20) {
		return this.#page<AdminUserDto>("/api/v1/admin/users", {
			q: query.trim() || undefined,
			page,
			pageSize,
		});
	}

	adminGetUser(userId: string) {
		return this.#get<AdminUserDetailDto>(`/api/v1/admin/users/${userId}`);
	}

	adminCreateUser(input: {
		fullName: string;
		email: string;
		status: "active" | "invited" | "suspended";
		memberships: Array<{ organisationId: string; role: string }>;
	}) {
		return this.#post<AdminUserDetailDto>("/api/v1/admin/users", input);
	}

	adminUpdateUser(
		userId: string,
		input: { fullName?: string; status?: "active" | "invited" | "suspended" | "closed" },
	) {
		return this.#patch<AdminUserDetailDto>(`/api/v1/admin/users/${userId}`, input);
	}

	adminPutUserMemberships(userId: string, memberships: Array<{ organisationId: string; role: string }>) {
		return this.#put<AdminUserDetailDto>(`/api/v1/admin/users/${userId}/memberships`, { memberships });
	}

	adminGetCustomer(organisationId: string) {
		return this.#get<{
			organisation: OrganisationDto;
			members: MemberDto[];
			subscription: SubscriptionDto | null;
			storage: StorageDto;
			domains: DomainDto[];
			mailboxes: MailboxDto[];
			invoices: InvoiceDto[];
		}>(`/api/v1/admin/customers/${organisationId}`);
	}

	adminListJobs(status?: string) {
		return this.#get<AdminProvisioningJobDto[]>("/api/v1/admin/jobs", { status });
	}

	adminGetJob(id: string) {
		return this.#get<ProvisioningJobDetailDto>(`/api/v1/admin/jobs/${id}`);
	}

	adminRetryJob(id: string) {
		return this.#post<AdminProvisioningJobDto>(`/api/v1/admin/jobs/${id}/retry`);
	}

	adminListAbuseEvents(status?: string) {
		return this.#get<AbuseEventDto[]>("/api/v1/admin/abuse", { status });
	}

	adminGetAbuseEvent(id: string) {
		return this.#get<AbuseEventDetailDto>(`/api/v1/admin/abuse/${id}`);
	}

	adminUpdateAbuseEvent(id: string, input: { status: string; actionTaken?: string }) {
		return this.#patch<AbuseEventDto>(`/api/v1/admin/abuse/${id}`, input);
	}

	adminMailHealth() {
		return this.#get<MailHealthDto>("/api/v1/admin/mail-health");
	}

	adminListMigrations(status?: string) {
		return this.#get<AdminMigrationDto[]>("/api/v1/admin/migrations", { status });
	}

	adminAuditLog(query?: { organisationId?: string; cursor?: string | null }) {
		return this.#page<AuditEntryDto>("/api/v1/admin/audit", query);
	}

	adminListDomains(query?: { q?: string; health?: string; provisioningState?: string; organisationId?: string }) {
		return this.#get<AdminDomainDto[]>("/api/v1/admin/domains", query);
	}

	adminCheckDomainDns(domainId: string) {
		return this.#post<AdminDomainDto>(`/api/v1/admin/domains/${domainId}/check-dns`);
	}

	/** `reason` is stored on the audit entry: resync overwrites Bafe's record
	 * of a customer's provisioning state, so it has to be explained. */
	adminResyncDomain(domainId: string, reason: string) {
		return this.#post<DomainDto>(`/api/v1/admin/domains/${domainId}/resync`, { reason });
	}

	adminListMailboxes(query?: { q?: string; status?: string; organisationId?: string }) {
		return this.#get<AdminMailboxDto[]>("/api/v1/admin/mailboxes", query);
	}

	adminSuspendMailbox(mailboxId: string, reason: string) {
		return this.#post<MailboxDto>(`/api/v1/admin/mailboxes/${mailboxId}/suspend`, { reason });
	}

	adminResumeMailbox(mailboxId: string, reason: string) {
		return this.#post<MailboxDto>(`/api/v1/admin/mailboxes/${mailboxId}/resume`, { reason });
	}

	adminListSubscriptions(query?: { status?: string; paymentIssuesOnly?: boolean }) {
		return this.#get<AdminSubscriptionDto[]>("/api/v1/admin/subscriptions", query);
	}

	adminListSupportCases(query?: { status?: string; priority?: string; organisationId?: string }) {
		return this.#get<AdminSupportCaseDto[]>("/api/v1/admin/support/cases", query);
	}

	adminListIncidents(status?: string) {
		return this.#get<IncidentDto[]>("/api/v1/admin/incidents", { status });
	}

	adminCreateIncident(input: {
		title: string;
		status: IncidentStatus;
		components: StatusPageComponent[];
		note: string;
	}) {
		return this.#post<IncidentDto>("/api/v1/admin/incidents", input);
	}

	adminUpdateIncident(id: string, input: { status: IncidentStatus; note: string }) {
		return this.#patch<IncidentDto>(`/api/v1/admin/incidents/${id}`, input);
	}

	adminListCustomerNotes(organisationId: string) {
		return this.#get<AdminNoteDto[]>(`/api/v1/admin/customers/${organisationId}/notes`);
	}

	adminCreateCustomerNote(organisationId: string, body: string) {
		return this.#post<AdminNoteDto>(`/api/v1/admin/customers/${organisationId}/notes`, { body });
	}

	adminCustomerStorageTrend(organisationId: string, windowDays?: number) {
		return this.#get<StorageTrendDto>(`/api/v1/admin/customers/${organisationId}/storage-trend`, {
			days: windowDays,
		});
	}

	adminGetConfiguration() {
		return this.#get<AdminConfigurationDto>("/api/v1/admin/configuration");
	}

	adminUpdateSetting(key: string, input: { value: string; reason: string }) {
		return this.#patch<AdminSettingDto>(`/api/v1/admin/configuration/settings/${key}`, input);
	}

	adminUpdateFeatureFlag(
		key: string,
		input: {
			scope: "global" | "plan" | "organisation";
			value: boolean;
			planCode?: string;
			organisationId?: string;
			reason: string;
		},
	) {
		return this.#patch<AdminFeatureFlagDto>(`/api/v1/admin/configuration/flags/${key}`, input);
	}

	getWebPushVapidPublicKey() {
		return this.#get<WebPushVapidPublicKeyDto>("/api/v1/mail/push/vapid-public-key");
	}

	getWebPushSettings() {
		return this.#get<WebPushSettingsDto>("/api/v1/mail/push/settings");
	}

	updateWebPushSettings(input: {
		enabled: boolean;
		soundEnabled: boolean;
		mailboxes: Array<{ mailboxId: string; enabled: boolean }>;
	}) {
		return this.#request<{ ok: true }>("PUT", "/api/v1/mail/push/settings", { body: input });
	}

	sendWebPushTest() {
		return this.#post<{ sent: number }>("/api/v1/mail/push/test", {});
	}

	subscribeWebPush(input: {
		endpoint: string;
		keys: { p256dh: string; auth: string };
	}) {
		return this.#post<{ ok: true }>("/api/v1/mail/push/subscribe", input);
	}

	unsubscribeWebPush(endpoint: string) {
		return this.#request<{ ok: true }>("DELETE", "/api/v1/mail/push/subscribe", { body: { endpoint } });
	}
}

/**
 * Reached through `globalThis` rather than the DOM global, so this module also
 * typechecks in the Node processes that import it for its wire types.
 */
function readCookie(name: string): string | null {
	const cookie = (globalThis as { document?: { cookie?: string } }).document?.cookie;
	if (!cookie) return null;
	const match = cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match?.[1] ? decodeURIComponent(match[1]) : null;
}
